import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { signOut } from '@/lib/auth-client';
import { requirePlatformPrincipal } from '@/lib/auth-guard';
import { Button } from '@/components/ui/button';

/**
 * `/_platform` layout route — guards all `/platform/*` children.
 *
 * Auth rules:
 *   1. Must have an active BetterAuth session, else redirect to /login.
 *   2. Must be a PLATFORM principal (probed via `GET /api/platform/whoami`,
 *      which the platformMiddleware rejects with 403 for org users).
 *   3. Org principals hitting this route get redirected to `/`.
 *
 * Does NOT require `organizationId` on the session (that's the `_authenticated`
 * check's job). Platform users have none — resolving them via principals
 * happens server-side.
 */
export const Route = createFileRoute('/platform')({
  beforeLoad: ({ location }) => requirePlatformPrincipal(location),
  component: PlatformLayout,
});

function PlatformLayout(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    // Drop caches outright — a subsequent login on the same tab should
    // never see the platform user's cached whoami / listings.
    queryClient.clear();
    navigate({ to: '/login' });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold">VagusX Platform</h1>
          <nav className="flex items-center gap-4 text-sm">
            <a href="/platform/organizations" className="hover:underline">
              Organizations
            </a>
            <a href="/platform/admin-users" className="hover:underline">
              Platform users
            </a>
            <Button variant="ghost" size="sm" onClick={handleSignOut} className="gap-1.5">
              <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
              Log out
            </Button>
          </nav>
        </div>
      </header>
      <main className="p-6">
        <Outlet />
      </main>
    </div>
  );
}
